import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  updatePassword,
  updateEmail,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  deleteUser,
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
  checkActionCode,
  type ActionCodeSettings,
  type User as FirebaseUser,
  type ActionCodeInfo
} from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { handleFirebaseError, createAuthError } from '../../lib/errors';
import { AuthErrorCode } from '../../types/auth';
import { userService } from './userService';
import { cascadeDeleteUserData } from './cascadeDeleteService';
import { syncUserEmailInAllProjects, removeMemberFromAllProjects } from '../../lib/firebase/projectService';
import type {
  SignInCredentials,
  SignUpCredentials,
  AuthResult,
  User,
  PasswordResetRequest
} from '../../types/auth';

const isBrowser = typeof window !== 'undefined';

/**
 * Get application URL for action code settings
 */
function getAppUrl(): string {
  if (!isBrowser) return '';
  return window.location.origin;
}

export const authService = {
  /**
   * Sign in with email and password
   */
  async signInWithEmail(credentials: SignInCredentials): Promise<AuthResult<User>> {
    if (!isBrowser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    try {
      console.log('[authService.signInWithEmail] Attempting login for:', credentials.email);
      
      // Normalize email to lowercase to ensure consistency
      const normalizedEmail = credentials.email.toLowerCase().trim();
      console.log('[authService.signInWithEmail] Normalized email:', normalizedEmail);

      console.log('[authService.signInWithEmail] Calling signInWithEmailAndPassword...');
      const { user: firebaseUser } = await signInWithEmailAndPassword(
        auth,
        normalizedEmail,
        credentials.password
      );
      console.log('[authService.signInWithEmail] Firebase auth successful, uid:', firebaseUser.uid);

      // Check email verification (skip for Google users)
      const isGoogleUser = firebaseUser.providerData.some(p => p.providerId === 'google.com');
      if (!isGoogleUser && !firebaseUser.emailVerified) {
        console.log('[authService.signInWithEmail] Email not verified');
        return {
          success: false,
          error: createAuthError(
            AuthErrorCode.EMAIL_NOT_VERIFIED,
            'Підтвердіть ваш email перед входом'
          ),
          data: await userService.convertFirebaseUser(firebaseUser)
        };
      }

      // Get or create user in Firestore
      console.log('[authService.signInWithEmail] Getting user from Firestore...');
      let user = await userService.getUser(firebaseUser.uid);

      if (!user) {
        console.log('[authService.signInWithEmail] User not in Firestore, creating...');
        // New user - create in Firestore с displayName
        const displayName = firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Користувач';
        user = await userService.createUser(firebaseUser, 'pending', {
          displayName
        });
      } else {
        console.log('[authService.signInWithEmail] User found in Firestore:', user.id);
      }

      return { success: true, data: user };
    } catch (error) {
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Sign in with Google
   */
  async signInWithGoogle(): Promise<AuthResult<User>> {
    if (!isBrowser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    // Check if Firebase Auth is initialized
    if (!auth) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'Firebase Auth не ініціалізовано')
      };
    }

    // Debug: Log emulator status
    console.log('[authService.signInWithGoogle] Auth emulator config:', (auth as any).emulatorConfig || 'production');
    console.log('[authService.signInWithGoogle] Project ID:', auth.app.options.projectId);

    try {
      const provider = new GoogleAuthProvider();
      
      // Add scopes for better user experience
      provider.addScope('profile');
      provider.addScope('email');
      
      // Set custom parameters
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      console.log('[authService.signInWithGoogle] Opening Google popup...');
      const { user: firebaseUser } = await signInWithPopup(auth, provider);
      console.log('[authService.signInWithGoogle] Google sign-in successful, uid:', firebaseUser.uid);
      
      // Extract displayName from provider data (Google might not return it in firebaseUser.displayName immediately)
      const googleProviderData = firebaseUser.providerData.find(p => p.providerId === 'google.com');
      const googleDisplayName = googleProviderData?.displayName || firebaseUser.displayName || '';
      
      console.log('[authService.signInWithGoogle] Google user data:', {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: googleDisplayName
      });

      // Get or create user in Firestore
      let user = await userService.getUser(firebaseUser.uid);

      if (!user) {
        // New user - create in Firestore with Google profile data
        const googleDisplayName = googleProviderData?.displayName || firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Користувач';
        console.log('[authService.signInWithGoogle] Creating new user with displayName:', googleDisplayName);
        user = await userService.createUser(firebaseUser, 'pending', {
          displayName: googleDisplayName
        });
      } else if (!user.displayName || user.displayName.trim() === '') {
        // Update displayName if it's empty
        const googleDisplayName = googleProviderData?.displayName || firebaseUser.displayName || '';
        if (googleDisplayName) {
          await userService.updateUser(firebaseUser.uid, {
            displayName: googleDisplayName
          });
          const updatedUser = await userService.getUser(firebaseUser.uid);
          if (updatedUser) {
            user = updatedUser;
          }
        }
      }

      return { success: true, data: user };
    } catch (error: unknown) {
      const firebaseError = error as { code?: string; message?: string };
      
      // Handle popup blocked error
      if (firebaseError.code === 'auth/popup-blocked') {
        return {
          success: false,
          error: createAuthError(
            AuthErrorCode.POPUP_BLOCKED,
            'Спливаюче вікно заблоковано браузером. Дозвольте спливаючі вікна для цього сайту або спробуйте інший браузер.'
          )
        };
      }
      
      // Handle popup closed by user
      if (firebaseError.code === 'auth/popup-closed-by-user') {
        return {
          success: false,
          error: createAuthError(
            AuthErrorCode.POPUP_CLOSED,
            'Вікно авторизації закрито. Спробуйте ще раз.'
          )
        };
      }
      
      // Handle cancelled popup
      if (firebaseError.code === 'auth/cancelled-popup-request') {
        return {
          success: false,
          error: createAuthError(
            AuthErrorCode.POPUP_CANCELLED,
            'Запит авторизації скасовано. Спробуйте ще раз.'
          )
        };
      }
      
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Sign up with email and password
   */
  async signUp(credentials: SignUpCredentials): Promise<AuthResult<User>> {
    if (!isBrowser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    try {
      // Normalize email to lowercase to ensure consistency
      const normalizedEmail = credentials.email.toLowerCase().trim();
      console.log('[authService.signUp] Creating user with email:', normalizedEmail);
      
      // Сначала создаём пользователя в Firebase Auth
      const { user: firebaseUser } = await createUserWithEmailAndPassword(
        auth,
        normalizedEmail,
        credentials.password
      );
      console.log('[authService.signUp] Firebase user created, uid:', firebaseUser.uid);

      // Сначала обновляем профиль в Firebase Auth - это важно сделать ДО создания Firestore документа
      // чтобы trigger успел получить актуальные данные
      await updateProfile(firebaseUser, { displayName: credentials.displayName });
      console.log('[authService.signUp] Profile updated with displayName:', credentials.displayName);

      // Принудительно перезагружаем пользователя, чтобы получить актуальные данные профиля
      // Это важно для синхронизации с Firebase Auth перед созданием Firestore документа
      await firebaseUser.reload();
      const refreshedUser = auth.currentUser;
      console.log('[authService.signUp] User reloaded, displayName:', refreshedUser?.displayName);

      // Send verification email
      await this.sendVerificationEmail(firebaseUser);

      // Create Firestore document with pending role
      console.log('[authService.signUp] Creating Firestore document...');
      const user = await userService.createUser(firebaseUser, 'pending', {
        displayName: refreshedUser?.displayName || credentials.displayName
      });
      console.log('[authService.signUp] User created successfully:', user.id);

      return { success: true, data: user };
    } catch (error) {
      console.error('[authService.signUp] Error during signUp:', error);
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Sign out
   */
  async signOut(): Promise<AuthResult<void>> {
    if (!isBrowser) return { success: true };

    try {
      console.log('[authService.signOut] Before signOut - currentUser:', auth.currentUser?.uid || 'null');
      await firebaseSignOut(auth);
      console.log('[authService.signOut] After signOut - currentUser:', auth.currentUser?.uid || 'null');
      return { success: true };
    } catch (error) {
      console.error('[authService.signOut] Error during signOut:', error);
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Reset password
   */
  async resetPassword(request: PasswordResetRequest): Promise<AuthResult<void>> {
    if (!isBrowser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    try {
      const appUrl = getAppUrl();
      // Use custom auth action page for password reset
      const actionCodeSettings: ActionCodeSettings = {
        url: `${appUrl}/auth-action?mode=resetPassword`,
        handleCodeInApp: false
      };

      await sendPasswordResetEmail(auth, request.email, actionCodeSettings);
      return { success: true };
    } catch (error) {
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Change password
   */
  async changePassword(newPassword: string): Promise<AuthResult<void>> {
    if (!isBrowser || !auth.currentUser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.NOT_AUTHENTICATED, 'Користувач не авторизований')
      };
    }

    try {
      await updatePassword(auth.currentUser, newPassword);
      return { success: true };
    } catch (error: unknown) {
      const firebaseError = error as { code?: string };
      if (firebaseError.code === 'auth/requires-recent-login') {
        return {
          success: false,
          error: createAuthError(
            AuthErrorCode.REQUIRES_RECENT_LOGIN,
            'Потрібна повторна авторизація для зміни пароля'
          )
        };
      }
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Change email
   */
  async changeEmail(newEmail: string): Promise<AuthResult<void>> {
    if (!isBrowser || !auth.currentUser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.NOT_AUTHENTICATED, 'Користувач не авторизований')
      };
    }

    try {
      const oldEmail = auth.currentUser.email || '';
      const displayName = auth.currentUser.displayName || '';
      
      await updateEmail(auth.currentUser, newEmail);

      // Update email in Firestore
      await userService.updateUser(auth.currentUser.uid, { email: newEmail });
      
      // Sync email and name in all projects where user is a member
      await syncUserEmailInAllProjects(auth.currentUser.uid, newEmail, displayName);

      // Send verification to new email
      await this.sendVerificationEmail(auth.currentUser);

      return { success: true };
    } catch (error: unknown) {
      const firebaseError = error as { code?: string };
      if (firebaseError.code === 'auth/requires-recent-login') {
        return {
          success: false,
          error: createAuthError(
            AuthErrorCode.REQUIRES_RECENT_LOGIN,
            'Потрібна повторна авторизація для зміни email'
          )
        };
      }
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Delete account with cascade deletion of all user data
   * Использует Cloud Function для удаления из Auth без requires-recent-login
   */
  async deleteAccount(): Promise<AuthResult<void>> {
    if (!isBrowser || !auth.currentUser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.NOT_AUTHENTICATED, 'Користувач не авторизований')
      };
    }

    try {
      const uid = auth.currentUser.uid;
      console.log('[authService.deleteAccount] Starting deletion for uid:', uid);

      // Вызываем Cloud Function для полного удаления аккаунта
      // Это работает без requires-recent-login
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../../lib/firebase');
      const deleteUserAccountFn = httpsCallable(functions, 'deleteUserAccount');
      
      console.log('[authService.deleteAccount] Calling Cloud Function...');
      const result = await deleteUserAccountFn({}) as { data: { success?: boolean; error?: string } };
      
      console.log('[authService.deleteAccount] Cloud Function result:', result);
      
      if (result.data?.success) {
        console.log('[authService.deleteAccount] Account deleted successfully via Cloud Function');
        return { success: true };
      } else {
        throw new Error(result.data?.error || 'Cloud Function returned error');
      }
    } catch (error: unknown) {
      console.error('[authService.deleteAccount] Final error:', error);
      const firebaseError = error as { code?: string; message?: string; details?: unknown };
      
      // Если ошибка от Cloud Function
      if (firebaseError.details) {
        const details = firebaseError.details as { message?: string };
        return { 
          success: false, 
          error: createAuthError(
            'custom' as AuthErrorCode, 
            details?.message || 'Помилка при видаленні акаунту'
          ) 
        };
      }
      
      if (firebaseError.code === 'auth/requires-recent-login') {
        return {
          success: false,
          error: createAuthError(
            AuthErrorCode.REQUIRES_RECENT_LOGIN,
            'Потрібна повторна авторизація для видалення акаунту'
          )
        };
      }
      
      // Если ошибка - возвращаем её
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Send verification email
   */
  async sendVerificationEmail(firebaseUser: FirebaseUser | null = auth.currentUser): Promise<AuthResult<void>> {
    if (!isBrowser || !firebaseUser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.NOT_AUTHENTICATED, 'Користувач не авторизований')
      };
    }

    try {
      const appUrl = getAppUrl();
      // Используем handleCodeInApp: false для отправки через email
      const actionCodeSettings: ActionCodeSettings = {
        url: `${appUrl}/role-selection`, // Куда редиректить ПОСЛЕ верификации
        handleCodeInApp: false // Отправлять через email
      };

      await sendEmailVerification(firebaseUser, actionCodeSettings);
      return { success: true };
    } catch (error) {
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Apply action code (for email verification, password reset, etc.)
   */
  async applyActionCode(code: string): Promise<AuthResult<void>> {
    if (!isBrowser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    try {
      await applyActionCode(auth, code);
      return { success: true };
    } catch (error) {
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Get current Firebase user
   */
  getCurrentUser(): FirebaseUser | null {
    return isBrowser ? auth.currentUser : null;
  },

  /**
   * Verify password reset code and get email
   */
  async verifyPasswordResetCode(code: string): Promise<AuthResult<string>> {
    if (!isBrowser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    try {
      const email = await verifyPasswordResetCode(auth, code);
      return { success: true, data: email };
    } catch (error) {
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Confirm password reset with new password
   */
  async confirmPasswordReset(code: string, newPassword: string): Promise<AuthResult<void>> {
    if (!isBrowser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    try {
      await confirmPasswordReset(auth, code, newPassword);
      return { success: true };
    } catch (error) {
      return { success: false, error: handleFirebaseError(error) };
    }
  },

  /**
   * Check action code and get action info
   */
  async checkActionCode(code: string): Promise<AuthResult<ActionCodeInfo>> {
    if (!isBrowser) {
      return {
        success: false,
        error: createAuthError(AuthErrorCode.FIREBASE_NOT_CONFIGURED, 'SSR не підтримується')
      };
    }

    try {
      const info = await checkActionCode(auth, code);
      return { success: true, data: info };
    } catch (error) {
      return { success: false, error: handleFirebaseError(error) };
    }
  }
};
